import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';

const ClientsSection = styled.section`
  background-color: white;
  padding: 100px 0;
`;

const ClientsContainer = styled.div`
  width: 92%;
  max-width: 1300px;
  margin: 0 auto;
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 70px;
`;

const SectionSubtitle = styled(motion.span)`
  display: inline-block;
  font-size: 1rem;
  font-weight: 600;
  color: var(--secondary-color);
  margin-bottom: 12px;
  position: relative;
  
  &:before, &:after {
    content: '';
    position: absolute;
    top: 50%;
    height: 1px;
    width: 30px;
    background-color: var(--secondary-color);
    transform: translateY(-50%);
  }
  
  &:before {
    right: calc(100% + 10px);
  }
  
  &:after {
    left: calc(100% + 10px);
  }
`;

const SectionTitle = styled(motion.h2)`
  font-size: 2.5rem;
  font-weight: 700;
  color: var(--text-dark);
  margin-bottom: 20px;
  
  span {
    color: var(--primary-color);
  }
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const SectionDescription = styled(motion.p)`
  font-size: 1.1rem;
  color: var(--text-muted);
  max-width: 700px;
  margin: 0 auto;
  line-height: 1.7;
`;

const ClientsMarqueeContainer = styled.div`
  position: relative;
  width: 100%;
  overflow: hidden;
  margin: 0 auto;
`;

const MarqueeTrack = styled.div`
  display: flex;
  gap: 40px;
  transition: transform 0.5s ease-out;
  transform: translateX(${props => props.position}px);
  padding: 10px 0;
`;

const ClientItem = styled(motion.div)`
  background-color: white;
  border-radius: 10px;
  padding: 25px 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--grey-200);
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
  min-width: 220px;
  height: 140px;
  flex-shrink: 0;
  
  img {
    max-width: 180px;
    max-height: 90px;
    object-fit: contain;
  }
  
  &:hover {
    border-color: var(--secondary-color);
  }
`;

const ClientControls = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 50px;
  gap: 15px;
`;

const ControlButton = styled.button`
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background-color: ${({ $active }) => ($active ? 'var(--primary-color)' : 'white')};
  border: 1px solid ${({ $active }) => ($active ? 'transparent' : 'var(--grey-200)')};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ $active }) => ($active ? 'white' : 'var(--text-muted)')};
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
  
  &:hover {
    background-color: var(--primary-color);
    color: white;
    transform: translateY(-3px);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  svg {
    font-size: 1.2rem;
  }
`;

const ProgressBar = styled.div`
  width: 100%;
  max-width: 300px;
  height: 6px;
  background-color: var(--grey-200);
  border-radius: 3px;
  margin: 30px auto 0;
  position: relative;
  overflow: hidden;
`;

const ProgressIndicator = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: ${props => props.width}%;
  background-color: var(--primary-color);
  border-radius: 3px;
  transition: width 0.3s ease, transform 0.3s ease;
`;

const ClientDots = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 15px;
  gap: 10px;
`;

const Dot = styled.button`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background-color: ${({ $active }) => ($active ? 'var(--primary-color)' : 'var(--grey-200)')};
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background-color: ${({ $active }) => ($active ? 'var(--primary-color)' : 'var(--grey-300)')};
  }
`;

// Removed client categories as requested

// Removed category button as requested

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { duration: 0.6 }
  }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2
    }
  }
};

const Clients = () => {
  const [position, setPosition] = useState(0);
  const [autoScroll, setAutoScroll] = useState(true);
  const [currentPage, setCurrentPage] = useState(0);
  const [progressWidth, setProgressWidth] = useState(0);
  const trackRef = useRef(null);
  const containerRef = useRef(null);
  
  // Extended list of 36 clients for IT/software company Websigma
  const clients = [
    // Original 12 clients
    {
      id: 1,
      name: "Shopify",
      category: "ecommerce",
      logo:"https://websigma.in/wp-content/uploads/2024/06/36.png",
    },
    {
      id: 2,
      name: "Slack",
      category: "tech",
      logo:"https://websigma.in/wp-content/uploads/2024/06/37.png",
    },
    {
      id: 3,
      name: "Zendesk",
      category: "saas",
      logo: "https://websigma.in/wp-content/uploads/2024/06/34.png",
    },
    {
      id: 4,
      name: "Mailchimp",
      category: "marketing",
      logo: "https://websigma.in/wp-content/uploads/2024/06/35.png",
    },
    {
      id: 5,
      name: "Hubspot",
      category: "marketing",
      logo: "https://websigma.in/wp-content/uploads/2024/06/32.png",
    },
    {
      id: 6,
      name: "Atlassian",
      category: "tech",
      logo: "https://websigma.in/wp-content/uploads/2024/06/33.png",
    },
    {
      id: 7,
      name: "Asana",
      category: "productivity",
      logo: "https://websigma.in/wp-content/uploads/2024/06/30.png",
    },
    {
      id: 8,
      name: "Zoom",
      category: "communications",
      logo: "https://websigma.in/wp-content/uploads/2024/06/31.png",
    },
    {
      id: 9,
      name: "Stripe",
      category: "fintech",
      logo: "https://websigma.in/wp-content/uploads/2024/06/29.png",
    },
    {
      id: 10,
      name: "MongoDB",
      category: "database",
      logo: "https://websigma.in/wp-content/uploads/2024/06/22.png",
    },
    {
      id: 11,
      name: "Square",
      category: "fintech",
      logo: "https://websigma.in/wp-content/uploads/2024/06/25.png",
    },
    {
      id: 12,
      name: "Digital Ocean",
      category: "cloud",
      logo: "https://websigma.in/wp-content/uploads/2024/06/28.png",
    },
    // Additional 24 clients to make 36 total
    {
      id: 13,
      name: "AWS",
      category: "cloud",
      logo: "https://websigma.in/wp-content/uploads/2024/06/20-ir.png",
    },
    {
      id: 14,
      name: "Google Cloud",
      category: "cloud",
      logo: "https://websigma.in/wp-content/uploads/2024/06/19-skin.png",
    },
    {
      id: 15,
      name: "Microsoft Azure",
      category: "cloud",
      logo: "https://websigma.in/wp-content/uploads/2024/06/21-decure.png",
    },
    {
      id: 16,
      name: "Salesforce",
      category: "crm",
      logo: "https://websigma.in/wp-content/uploads/2024/06/17.png",
    },
    {
      id: 17,
      name: "Adobe",
      category: "design",
      logo: "https://websigma.in/wp-content/uploads/2024/06/18-hrb.png",
    },
    {
      id: 18,
      name: "Dropbox",
      category: "storage",
      logo: "https://websigma.in/wp-content/uploads/2024/06/17-bj.png",
    },
    {
      id: 19,
      name: "PayPal",
      category: "fintech",
      logo: "https://websigma.in/wp-content/uploads/2024/06/16-gn.png",
    },
    {
      id: 20,
      name: "Twilio",
      category: "communications",
      logo: "https://websigma.in/wp-content/uploads/2024/06/13-br.png",
    },
    {
      id: 21,
      name: "Canva",
      category: "design",
      logo: "https://websigma.in/wp-content/uploads/2024/06/14-mf.png",
    },
    {
      id: 22,
      name: "Spotify",
      category: "entertainment",
      logo: "https://websigma.in/wp-content/uploads/2024/06/11-ct.png",
    },
    {
      id: 23,
      name: "Netflix",
      category: "entertainment",
      logo: "https://websigma.in/wp-content/uploads/2024/06/11.png",
    },
    {
      id: 24,
      name: "Airbnb",
      category: "travel",
      logo: "https://websigma.in/wp-content/uploads/2024/06/12-nb.png",
    },
    {
      id: 25,
      name: "Uber",
      category: "transportation",
      logo: "https://websigma.in/wp-content/uploads/2024/06/10-vista.png",
    },
    {
      id: 26,
      name: "LinkedIn",
      category: "social",
      logo: "https://websigma.in/wp-content/uploads/2024/06/9-sms.png",
    },
    {
      id: 27,
      name: "Twitter",
      category: "social",
      logo: "https://websigma.in/wp-content/uploads/2024/06/7-ion.png",
    },
    {
      id: 28,
      name: "Meta",
      category: "social",
      logo: "https://websigma.in/wp-content/uploads/2024/06/7.png",
    },
    {
      id: 29,
      name: "Notion",
      category: "productivity",
      logo: "https://websigma.in/wp-content/uploads/2024/06/8-fs.png",
    },
    {
      id: 30,
      name: "Figma",
      category: "design",
      logo: "https://websigma.in/wp-content/uploads/2024/06/6-freshwork.png",
    },
    {
      id: 31,
      name: "GitHub",
      category: "development",
      logo: "https://websigma.in/wp-content/uploads/2024/06/6.png",
    },
    {
      id: 32,
      name: "GitLab",
      category: "development",
      logo: "https://websigma.in/wp-content/uploads/2024/06/4-infinite.png",
    },
    {
      id: 33,
      name: "Docker",
      category: "development",
      logo: "https://websigma.in/wp-content/uploads/2024/06/4.png",
    },
    {
      id: 34,
      name: "Kubernetes",
      category: "development",
      logo: "https://websigma.in/wp-content/uploads/2024/06/5-ofb.png",
    },
    {
      id: 35,
      name: "Terraform",
      category: "infrastructure",
      logo: "https://websigma.in/wp-content/uploads/2024/06/2-sleepyhead.png",
    },
    {
      id: 36,
      name: "Cloudflare",
      category: "security",
      logo: "https://websigma.in/wp-content/uploads/2024/06/3-hdi.png",
    }
  ];
  
  // Duplicate the clients array to create a seamless loop effect
  const duplicatedClients = [...clients, ...clients];
  
  useEffect(() => {
    let animationId;
    let lastTimestamp = 0;
    const speed = 0.2; // pixels per millisecond
    
    const animate = (timestamp) => {
      if (!lastTimestamp) lastTimestamp = timestamp;
      const elapsed = timestamp - lastTimestamp;
      
      if (autoScroll && trackRef.current && containerRef.current) {
        // Calculate the track width and container width
        const trackWidth = trackRef.current.scrollWidth / 2; // Half because we duplicated the items
        const itemWidth = 260; // Item width + gap
        const visibleItems = Math.floor(containerRef.current.offsetWidth / itemWidth);
        
        // Move the track to the left
        setPosition(prevPosition => {
          // Reset position when we've moved one full cycle
          if (Math.abs(prevPosition) >= trackWidth) {
            return 0;
          }
          const newPosition = prevPosition - speed * elapsed;
          
          // Update current page based on position
          const pageWidth = visibleItems * itemWidth;
          const pageCount = Math.ceil(clients.length / visibleItems);
          const newPage = Math.floor(Math.abs(newPosition) / pageWidth) % pageCount;
          
          if (newPage !== currentPage) {
            setCurrentPage(newPage);
          }
          
          return newPosition;
        });
      }
      
      lastTimestamp = timestamp;
      animationId = requestAnimationFrame(animate);
    };
    
    if (autoScroll) {
      animationId = requestAnimationFrame(animate);
    }
    
    return () => {
      if (animationId) {
        cancelAnimationFrame(animationId);
      }
    };
  }, [autoScroll, currentPage, clients.length]);
  
  // Calculate number of pages based on client count and visible items
  const getPageCount = () => {
    if (!containerRef.current) return 1;
    
    const itemWidth = 260; // Item width + gap (increased to match new size)
    const visibleItems = Math.floor(containerRef.current.offsetWidth / itemWidth);
    return Math.ceil(clients.length / visibleItems);
  };
  
  // Update progress bar width based on current page
  useEffect(() => {
    const pageCount = getPageCount();
    if (pageCount <= 1) {
      setProgressWidth(100);
    } else {
      const newWidth = ((currentPage + 1) / pageCount) * 100;
      setProgressWidth(newWidth);
    }
  }, [currentPage]);
  
  const handlePrev = () => {
    setAutoScroll(false);
    const itemWidth = 260; // Item width + gap (increased to match new size)
    const visibleItems = Math.floor(containerRef.current.offsetWidth / itemWidth);
    setPosition(prevPosition => prevPosition + (itemWidth * visibleItems));
    setCurrentPage(prev => (prev === 0 ? getPageCount() - 1 : prev - 1));
  };
  
  const handleNext = () => {
    setAutoScroll(false);
    const itemWidth = 260; // Item width + gap (increased to match new size)
    const visibleItems = Math.floor(containerRef.current.offsetWidth / itemWidth);
    setPosition(prevPosition => prevPosition - (itemWidth * visibleItems));
    setCurrentPage(prev => (prev === getPageCount() - 1 ? 0 : prev + 1));
  };
  
  const goToPage = (pageIndex) => {
    setAutoScroll(false);
    const itemWidth = 260; // Item width + gap (increased to match new size)
    const visibleItems = Math.floor(containerRef.current.offsetWidth / itemWidth);
    const newPosition = -(pageIndex * visibleItems * itemWidth);
    setPosition(newPosition);
    setCurrentPage(pageIndex);
  };

  return (
    <ClientsSection id="clients">
      <ClientsContainer>
        <SectionHeader
          as={motion.div}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          variants={staggerContainer}
        >
          <SectionSubtitle variants={fadeInUp}>Our Clients</SectionSubtitle>
          <SectionTitle variants={fadeInUp}>
            Trusted by <span>Leading Businesses</span>
          </SectionTitle>
          <SectionDescription variants={fadeInUp}>
            We are grateful to all our clients who provided an opportunity to offer our services. We thank all the startup to enterprise companies who trusted in our approach and team.
          </SectionDescription>
        </SectionHeader>
        
        {/* Client categories removed as requested */}
        
        <ClientsMarqueeContainer ref={containerRef}>
          <MarqueeTrack position={position} ref={trackRef}>
            {duplicatedClients.map((client, index) => (
              <ClientItem
                key={`${client.id}-${index}`}
                onMouseEnter={() => setAutoScroll(false)}
                onMouseLeave={() => setAutoScroll(true)}
              >
                <img src={client.logo} alt={client.name} />
              </ClientItem>
            ))}
          </MarqueeTrack>
        </ClientsMarqueeContainer>
        
        <ClientControls>
          <ControlButton 
            onClick={handlePrev}
            aria-label="Previous clients"
          >
            <FaChevronLeft />
          </ControlButton>
          <ControlButton 
            onClick={handleNext}
            aria-label="Next clients"
          >
            <FaChevronRight />
          </ControlButton>
        </ClientControls>
        
        <ProgressBar>
          <ProgressIndicator width={progressWidth} />
        </ProgressBar>
        
        <ClientDots>
          {Array.from({ length: getPageCount() }).map((_, index) => (
            <Dot 
              key={index} 
              $active={currentPage === index} 
              onClick={() => goToPage(index)}
              aria-label={`Go to client page ${index + 1}`}
            />
          ))}
        </ClientDots>
      </ClientsContainer>
    </ClientsSection>
  );
};

export default Clients;
